// src/components/ReprorationHistory.tsx
//
// ★★★ WAVE 13 (P7) — WHAT HAPPENED TO THIS BILL WHEN ITS OFFICER'S ENTITLEMENT
// CHANGED.
//
// P2C1 §12, Recovery Agency interface row 7: *"Alert file on re-prorated HSP
// bills due to changes in officers' entitlement records"*. This is the same
// information, for one bill, on the screen a person looks at when they are asked
// about it.
//
// ══ ⚠ A COMPONENT, NOT MARKUP INSIDE `BillDetailPage` ══
//
// The same reason `BillHistory` and `RateProvenanceNote` are components: the
// sentences below are the deliverable, and a sentence with nothing asserting it
// is one careless edit from becoming an over-claim. `BillDetailPage.dom.test.tsx`
// mounts `ComputePanel` alone — the page itself has too many live dependencies to
// mount — so markup left inline there would have no DOM coverage at all.
//
// ══ ★★★ THE THREE SENTENCES THIS COMPONENT EXISTS TO KEEP TRUE ══
//
//   1. **`REFERRED` means PRS changed NOTHING.** It is not a milder
//      re-proration; it is a re-proration that was refused because money had
//      already been instructed against the old division, or because an
//      adjustment chain hangs off the bill. A reader who took it for a
//      completed change would reconcile against a figure that never moved.
//   2. **The referral reason is the SERVER's**, verbatim. It says why this
//      product refused to re-divide money it had already instructed, and a
//      screen paraphrase would be a second, weaker statement of a refusal.
//   3. **The AMOUNT never changed.** Every entry re-divides one total. Without
//      that sentence a reader seeing two money figures move has no way to know
//      the bill still says what it always said.
import { plainProse } from '../lib/plain-words'
import type { ReactNode } from 'react'
import { Money } from './Money'
import { formatInstant } from '../lib/format'
import type { BillReproration } from '../lib/types'

/** What each closed `cause` value means, in words. The VALUE is what is stored. */
const CAUSE_LABEL: Readonly<Record<string, string>> = {
  ENTITLEMENT_CHANGED: "the officer's entitlement record changed",
  ENTITLEMENT_AND_RULE_CHANGED: "the officer's entitlement record changed, and so did the rule in force",
}

export function ReprorationHistory({ reprorations }: { reprorations: readonly BillReproration[] }): ReactNode {
  if (reprorations.length === 0) return null
  return (
    <div className="flex flex-col gap-3">
      {reprorations.map((r) => (
        <div key={r.id} className="pt-well p-4">
          <p className="font-semibold">
            {r.outcome === 'REPRORATED'
              ? 'Re-prorated: the split was re-divided'
              : 'Referred: the split was NOT changed, and a person must act'}
          </p>
          <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 max-w-2xl">
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Entitlement</dt>
            {/*
              ⚠ Rendered whole and unparsed. The digest is
              `<scheme>~<ward>~<validFrom>~<validUntil or "open">` and is readable
              on purpose (`entitlement-fingerprint.ts`): splitting it into four
              labelled fields here would be this screen deciding what the four
              parts mean, which is the server's statement to make.
            */}
            <dd className="pt-mono text-sm">
              {r.priorEntitlementDigest ?? 'None'} &rarr; {r.newEntitlementDigest}
            </dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Government subsidy</dt>
            <dd className="pt-tabular">
              <Money cents={r.priorSubsidy} /> &rarr; <Money cents={r.newSubsidy} />
            </dd>
            {/*
              ⚠ THE OFFICER'S CO-PAYMENT IS ALWAYS SHOWN. It is the figure a
              Recovery Agency deducts from payroll (P2C1 §8), so it is the half of
              the movement a person is most likely to be asked about.
            */}
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Officer co-payment</dt>
            <dd className="pt-tabular">
              <Money cents={r.priorCoPayment} /> &rarr; <Money cents={r.newCoPayment} />
            </dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Applied rule</dt>
            <dd className="pt-mono text-sm">
              {r.priorRule.id === null || r.priorRule.id === undefined ? 'None' : `${r.priorRule.id} v${r.priorRule.version ?? 'None'}`} &rarr; {r.newRule.id} v{r.newRule.version}
            </dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Why</dt>
            {/*
              ⚠ An UNRECOGNISED cause renders the raw value rather than a blank or
              a guess. The same discipline `hsp-outcome.ts` applies to a refusal
              kind it has never heard of: a stored token this build does not know
              is still a fact, and rendering nothing would hide it.
            */}
            <dd className="text-sm">{CAUSE_LABEL[r.cause] ?? r.cause}</dd>
            <dt className="text-sm text-[var(--sgds-body-color-subtle)]">Recorded</dt>
            <dd className="text-sm">{formatInstant(r.recordedAt)}</dd>
          </dl>
          {/* ⚠ VERBATIM — the server's own refusal, never paraphrased. */}
          {r.referralReason !== null && <p className="text-sm mt-3">{plainProse(r.referralReason)}</p>}
        </div>
      ))}
      <p className="text-sm text-[var(--sgds-body-color-subtle)]">
        The amount charged is unchanged by every entry above: a re-proration re-divides one amount between the
        government and the officer and never alters the total. An adjustment is what changes the total, and those are
        listed separately.
      </p>
    </div>
  )
}
